"use client";

/**
 * The guided path: four questions, a preview built from real answers, then
 * the price.
 *
 * WHY THE PRICE COMES LAST
 * A cold visitor does not care which subscription tier they belong in, they
 * care whether Maren can raise their score. Showing what Maren would
 * actually run for THEM first, using their own diagnostic misses and their
 * own exam date, turns the pricing screen into "buy this plan" rather than
 * "hit this paywall".
 *
 * NOTHING HERE IS THROWAWAY
 * Every question maps onto a field the planner already consumes, and the
 * answers persist to the account whether or not they buy.
 */

import { useMemo, useState } from "react";
import Link from "next/link";
import {
  questionById,
  isShaky,
  type DiagnosticAnswer,
} from "../../lib/diagnosticQuestions";
import { REASON_PHRASE, listOf, joinTopics } from "../../lib/diagnosticFindings";
import { STUDY_PASSES } from "../../lib/pricingPlans";
import {
  HOUR_BANDS,
  RESOURCE_OPTIONS,
  hourTargetFor,
  recommendedPlan,
  saveSetup,
  weeksUntil,
  type HourBandKey,
  type UserType,
} from "../../lib/diagnosticSetup";
import { flushSetup } from "../../lib/diagnosticSetup";
import { trackDiagnostic } from "../../lib/diagnosticAnalytics";
import type { DiagnosticUserState } from "../../lib/analytics";

type PlanChoice = "pass_3mo" | "pass_6mo" | "max";

const MONTHLY_PRICE = "$39";

export default function GuidedSetup({
  answers,
  signedIn,
  userState,
  onBack,
}: {
  answers: DiagnosticAnswer[];
  signedIn: boolean;
  userState: DiagnosticUserState;
  onBack: () => void;
}) {
  const [step, setStep] = useState<"setup" | "plan">("setup");
  const [examDate, setExamDate] = useState("");
  const [userType, setUserType] = useState<UserType | null>(null);
  const [resources, setResources] = useState<string[]>([]);
  const [hourBand, setHourBand] = useState<HourBandKey | null>(null);
  const [chosen, setChosen] = useState<PlanChoice | null>(null);

  const weeks = useMemo(() => weeksUntil(examDate || null), [examDate]);
  const recommended = useMemo(() => recommendedPlan(weeks), [weeks]);
  const selected = chosen ?? recommended;

  const ready = Boolean(examDate && userType && hourBand);

  const shaky = answers.filter(isShaky);
  const topics = joinTopics(
    shaky.map((a) => questionById(a.id)?.subtopic).filter(Boolean) as string[],
  );
  // REASON_PHRASE, not the chip labels: ERROR_TAG_SHORT.process_of_elimination
  // is "POE", which lowercases to "poe" and reads as a typo in a sentence.
  const reasons = listOf(
    shaky.flatMap((a) => a.reasons).map((r) => REASON_PHRASE[r]),
    2,
  );
  const resourceLabel = resources.length ? listOf(resources, 3) : "whatever you are using";

  function toggleResource(r: string) {
    setResources((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));
  }

  function goToPlan() {
    if (!ready || !userType || !hourBand) return;
    // Held locally either way. If they are signed in it goes to the account
    // immediately; if not, it waits for signup, which checkout will prompt.
    saveSetup({ examDate, userType, resources, hourBand });
    if (signedIn) void flushSetup();
    trackDiagnostic(userState, "diagnostic_setup_completed", {
      weeks_until_exam: weeks,
      user_type: userType,
      resource_count: resources.length,
      weekly_hours: hourTargetFor(hourBand),
    });
    trackDiagnostic(userState, "diagnostic_plan_viewed", {
      recommended_plan: recommended,
      weeks_until_exam: weeks,
    });
    setStep("plan");
    window.scrollTo({ top: 0 });
  }

  if (step === "setup") {
    return (
      <main className="flex-1 px-4 py-10">
        <div className="mx-auto max-w-2xl">
          <h1 className="font-display text-3xl leading-tight">
            Four questions, then Maren builds the plan.
          </h1>
          <p className="mt-3 text-maren-charcoal/70 dark:text-maren-stone/70">
            These are the inputs Maren needs. They stay on your account either way.
          </p>

          <Field label="When are you testing?">
            <input
              type="date"
              value={examDate}
              onChange={(e) => setExamDate(e.target.value)}
              className="w-full rounded-lg border border-maren-sage/40 dark:border-maren-sage/20 bg-white dark:bg-maren-charcoal px-3 py-2.5 text-base"
            />
          </Field>

          <Field label="Is this your first MCAT or a retake?">
            <div className="flex gap-2">
              {([
                { key: "first_time", label: "First time" },
                { key: "retaker", label: "Retaking it" },
              ] as const).map((o) => (
                <Choice
                  key={o.key}
                  on={userType === o.key}
                  onClick={() => setUserType(o.key)}
                  label={o.label}
                />
              ))}
            </div>
          </Field>

          <Field label="What are you studying with? Pick any that apply.">
            <div className="flex flex-wrap gap-2">
              {RESOURCE_OPTIONS.map((r) => (
                <Choice
                  key={r}
                  on={resources.includes(r)}
                  onClick={() => toggleResource(r)}
                  label={r}
                  grow={false}
                />
              ))}
            </div>
          </Field>

          <Field label="How many hours a week can you actually study?">
            <div className="flex flex-wrap gap-2">
              {HOUR_BANDS.map((b) => (
                <Choice
                  key={b.key}
                  on={hourBand === b.key}
                  onClick={() => setHourBand(b.key)}
                  label={b.label}
                />
              ))}
            </div>
          </Field>

          <button
            type="button"
            disabled={!ready}
            onClick={goToPlan}
            className="mt-6 w-full rounded-lg bg-maren-green px-6 py-3.5 font-semibold text-maren-stone transition hover:bg-maren-green/90 disabled:opacity-30"
          >
            Show me the plan
          </button>
          <button
            type="button"
            onClick={onBack}
            className="mt-3 w-full rounded-lg px-6 py-2 text-sm text-maren-charcoal/60 dark:text-maren-stone/60 hover:underline"
          >
            Back to my result
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-1 px-4 py-10">
      <div className="mx-auto max-w-2xl">
        <h1 className="font-display text-3xl leading-tight">
          Here is what Maren would run for you.
        </h1>
        <p className="mt-3 text-maren-charcoal/70 dark:text-maren-stone/70">
          {weeks !== null ? `${weeks} week${weeks === 1 ? "" : "s"} until your exam. ` : ""}
          {HOUR_BANDS.find((b) => b.key === hourBand)?.label} hours a week.
        </p>

        <div className="mt-6 rounded-xl border border-maren-sage/40 dark:border-maren-sage/20 bg-white dark:bg-maren-charcoal p-5">
          <ul className="list-disc pl-5 space-y-2 text-[15px]">
            <li>
              Start with {topics || "the areas your logs surface"}. That is where today&apos;s
              questions broke down.
            </li>
            <li>
              Watch your real practice for the way you missed them:{" "}
              {reasons || "repeat error patterns"}.
            </li>
            <li>Build your week around {resourceLabel}, and rebuild it when you fall behind.</li>
            <li>Turn every miss you log into review cards, and push them to Anki.</li>
            <li>Re-rank what matters most every time you add practice.</li>
          </ul>
        </div>

        <h2 className="mt-8 font-display text-2xl">Pick a window</h2>
        <p className="mt-1 text-sm text-maren-charcoal/60 dark:text-maren-stone/60">
          {weeks !== null && weeks <= 13
            ? "A 3 month pass covers you through test day."
            : weeks !== null
              ? "A 6 month pass covers you through test day."
              : "Passes are paid once with no rebill."}
        </p>

        <div className="mt-4 space-y-2">
          {STUDY_PASSES.map((p) => (
            <PriceRow
              key={p.id}
              id={p.id}
              name={p.name}
              price={p.price}
              sub={`${p.perMonth}, paid once`}
              selected={selected === p.id}
              recommended={recommended === p.id}
              onSelect={() => setChosen(p.id)}
            />
          ))}
          <PriceRow
            id="max"
            name="Monthly"
            price={MONTHLY_PRICE}
            sub="Cancel anytime"
            selected={selected === "max"}
            recommended={recommended === "max"}
            onSelect={() => setChosen("max")}
          />
        </div>

        {/* /billing/start handles the unauthenticated case itself: it sends
            them through login and preserves the plan in ?next=, so the
            choice survives the round trip. */}
        <Link
          href={`/billing/start?plan=${selected}`}
          className="mt-4 block w-full rounded-lg bg-maren-green px-6 py-3.5 text-center font-semibold text-maren-stone transition hover:bg-maren-green/90"
        >
          Continue with the {labelFor(selected)}
        </Link>

        <div className="mt-5 rounded-xl border border-maren-sage/40 dark:border-maren-sage/20 bg-white dark:bg-maren-charcoal p-5">
          <h3 className="font-display text-lg mb-2">Not ready to pay?</h3>
          <p className="mb-3 text-sm">
            Your exam date, resources and hours are already saved. Make a free account and you
            keep all of it plus your five diagnostic questions. You can switch Maren on whenever
            you want.
          </p>
          <Link
            href={signedIn ? "/dashboard" : "/signup"}
            className="block w-full rounded-lg border border-maren-sage/50 px-6 py-3 text-center font-semibold transition hover:border-maren-green"
          >
            Keep my setup and track free
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setStep("setup")}
          className="mt-4 w-full rounded-lg px-6 py-2 text-sm text-maren-charcoal/60 dark:text-maren-stone/60 hover:underline"
        >
          Change my answers
        </button>
      </div>
    </main>
  );
}

function labelFor(p: PlanChoice): string {
  if (p === "pass_3mo") return "3 month pass";
  if (p === "pass_6mo") return "6 month pass";
  return "monthly plan";
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mt-5 rounded-xl border border-maren-sage/40 dark:border-maren-sage/20 bg-white dark:bg-maren-charcoal p-5">
      <p className="mb-3 text-maren-charcoal/70 dark:text-maren-stone/70">{label}</p>
      {children}
    </div>
  );
}

function Choice({
  on,
  onClick,
  label,
  grow = true,
}: {
  on: boolean;
  onClick: () => void;
  label: string;
  grow?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${grow ? "flex-1 min-w-[104px]" : ""} rounded-lg border px-4 py-2.5 text-sm transition ${
        on
          ? "border-maren-green bg-maren-green/10 font-semibold"
          : "border-maren-sage/40 dark:border-maren-sage/20 hover:border-maren-green"
      }`}
    >
      {label}
    </button>
  );
}

function PriceRow({
  id,
  name,
  price,
  sub,
  selected,
  recommended,
  onSelect,
}: {
  id: string;
  name: string;
  price: string;
  sub: string;
  selected: boolean;
  recommended: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      data-plan={id}
      onClick={onSelect}
      className={`flex w-full items-baseline justify-between gap-3 rounded-lg border px-4 py-4 text-left transition ${
        selected
          ? "border-maren-green bg-maren-green/10"
          : "border-maren-sage/40 dark:border-maren-sage/20 hover:border-maren-green"
      }`}
    >
      <span>
        <span className="block">
          {name}{" "}
          {recommended && (
            <span className="text-sm text-maren-green dark:text-maren-gold">
              Covers your exam
            </span>
          )}
        </span>
        <span className="block text-sm text-maren-charcoal/60 dark:text-maren-stone/60">{sub}</span>
      </span>
      <span className="font-display text-2xl whitespace-nowrap">{price}</span>
    </button>
  );
}
